import { useEffect, useRef, useState } from "preact/hooks";
import { getLang, tr } from "./i18n.js";
import { get, html, IN_APP, post } from "./lib.js";
import { Badge, Loading, useAction } from "./ui.js";
import { guideText } from "./guide.js";

// The dashboard assistant: one chat, a drawer that opens from every page. Anyone on the team can
// ask how the dashboard works and get the steps from the user guide. An owner or admin can also
// say what to change about the AI agent: the assistant proposes it setting by setting, and
// nothing moves until Apply is pressed. The cards under the chat are where the old value and the
// new one stand next to each other.
//
// Other pages talk to it through two window events:
//   rz:assistant        open the drawer; `detail.text` puts a question in the composer
//   rz:config-applied   sent here after an Apply, so the AI agent page reloads its form

const OPEN_KEY = "rz.assistant";

const HOW_TO_EXAMPLES = [
  "How do I connect WhatsApp?",
  "How do I add a staff member and their working hours?",
  "Why didn't a customer get a reminder?",
  "What is this page for?",
];

const CHANGE_EXAMPLES = [
  "Customers keep asking about parking. There is free street parking behind the building.",
  "Make the tone a bit more formal, and tell the agent never to promise a discount.",
];

/** Opens the assistant from anywhere, optionally with a question ready to send. */
export function openAssistant(text) {
  window.dispatchEvent(new CustomEvent("rz:assistant", { detail: { text } }));
}

/** Sparkles (Material "auto_awesome"): the assistant's icon here and in the Android app's top bar. */
export function Sparkles() {
  return html`<svg class="sparkles" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
    <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zM11.5 9.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" />
  </svg>`;
}

/** The guide id for a dashboard path: the same ids <PageGuide> uses on each page. */
export function pageId(path) {
  if (path === "/") return "overview";
  const [first, second] = path.split("/").filter(Boolean);
  if (first === "settings") return `settings.${second || "business"}`;
  return first;
}

function remembered() {
  try {
    return localStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
}

function remember(open) {
  try {
    localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  } catch {
    /* private mode */
  }
}

/** A proposed value, in the words the form uses: hours for reminders, On/Off for a switch. */
function showValue(change, value) {
  if (value === null || value === undefined || value === "") return tr("(empty)");
  if (typeof value === "boolean") return value ? tr("On") : tr("Off");
  if (Array.isArray(value)) {
    if (change.field === "reminder_offsets") return value.map((m) => m / 60).join(", ") + " " + tr("hours before");
    return value.join(", ");
  }
  if (typeof value === "object") return `${value.title}\n${value.content}`;
  return String(value);
}

const ACTION_LABEL = {
  create: () => tr("New knowledge entry"),
  delete: () => tr("Remove knowledge entry"),
  update: () => tr("Knowledge entry"),
};

function ChangeCard({ change, busy, onApply, onDismiss }) {
  const kind = change.target === "agent" ? tr("Agent setting") : ACTION_LABEL[change.action]();
  const decided = change.status !== "pending";
  const statusLabel = { applied: tr("Applied"), dismissed: tr("Dismissed"), stale: tr("Out of date") }[change.status];
  return html`<div class="suggestion ${decided ? "decided" : ""}">
    <p class="small">
      <${Badge} kind=${decided ? "muted" : "accent"}>${kind}</${Badge}>
      ${change.label && html` <strong dir="auto">${tr(change.label)}</strong>`}
      ${change.summary && html` <span class="muted" dir="auto">${change.summary}</span>`}
      ${decided && html` <${Badge} kind=${change.status === "applied" ? "ok" : "muted"}>${statusLabel}</${Badge}>`}
    </p>
    ${!decided &&
    html`<div class="change-diff">
      <div><span class="hint">${tr("Now")}</span><div class="was" dir="auto">${showValue(change, change.before)}</div></div>
      <div><span class="hint">${tr("After applying")}</span><div class="will" dir="auto">${showValue(change, change.after)}</div></div>
    </div>`}
    ${!decided &&
    html`<div class="row-actions">
      <button class="btn small primary" disabled=${busy} onClick=${onApply}>${tr("Apply")}</button>
      <button class="btn small ghost" disabled=${busy} onClick=${onDismiss}>${tr("Dismiss")}</button>
    </div>`}
  </div>`;
}

/** Waiting proposals, for the badge on the button that opens the drawer. */
export function pendingCount(state) {
  return (state?.changes ?? []).filter((c) => c.status === "pending").length;
}

/**
 * The assistant's state, shared by the sidebar button (for its badge) and the drawer. Loaded once
 * per business, so the badge shows waiting proposals before anyone opens the chat.
 */
export function useAssistant(t) {
  const [open, setOpenState] = useState(remembered);
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState("");
  /** Positions of answers flagged in this chat; a New chat starts the count again. */
  const [reported, setReported] = useState(() => new Set());

  useEffect(() => {
    if (!t) return;
    let alive = true;
    setState(null);
    get(`${t.base}/config-chat`)
      .then((s) => alive && setState(s))
      .catch(() => alive && setState({ chat_id: null, messages: [], changes: [] }));
    return () => {
      alive = false;
    };
  }, [t?.id]);

  const setOpen = (next) => {
    setOpenState(next);
    remember(next);
  };

  useEffect(() => {
    const onOpen = (e) => {
      if (e.detail?.text) setDraft(e.detail.text);
      setOpen(true);
    };
    window.addEventListener("rz:assistant", onOpen);
    return () => window.removeEventListener("rz:assistant", onOpen);
  }, []);

  return { open, setOpen, state, setState, draft, setDraft, reported, setReported };
}

export function AssistantDrawer({ t, path, assistant }) {
  const { open, setOpen, state, setState, draft, setDraft, reported, setReported } = assistant;
  const [busy, run] = useAction();
  const bottom = useRef(null);
  const input = useRef(null);
  const admin = t.role !== "agent";
  const page = pageId(path);

  useEffect(() => bottom.current?.scrollIntoView({ block: "end" }), [state?.messages.length, busy, open]);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const refresh = async () => {
    try {
      setState(await get(`${t.base}/config-chat`));
    } catch {
      /* keep what is on screen */
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    setState((s) => ({ ...s, messages: [...(s?.messages ?? []), { role: "user", text }] }));
    try {
      setState(await run(() => post(`${t.base}/config-chat/messages`, { text, page, lang: getLang(), in_app: IN_APP })));
    } catch {
      // The toast said what went wrong; put their words back so nothing is lost.
      setDraft(text);
      await refresh();
    }
  };

  /** Returns false when the change didn't go through, which is what stops "Apply all". */
  const decide = async (change, action) => {
    try {
      setState(
        await run(
          () => post(`${t.base}/config-chat/changes/${change.id}/${action}`),
          action === "apply" ? tr("Change applied") : tr("Suggestion dropped"),
        ),
      );
      if (action === "apply") window.dispatchEvent(new Event("rz:config-applied"));
      return true;
    } catch {
      // The toast said why, usually that the setting moved since this was suggested; show it.
      await refresh();
      return false;
    }
  };

  const applyAll = async (pending) => {
    for (const change of pending) if (!(await decide(change, "apply"))) break;
  };

  // Google Play's AI-Generated Content policy: an answer can be flagged without leaving the app.
  const report = async (index) => {
    try {
      await run(() => post(`${t.base}/config-chat/report`, { index }), tr("Thanks. We'll look at this answer."));
      setReported((r) => new Set(r).add(index));
    } catch {
      /* the toast already said what went wrong */
    }
  };

  const newChat = async () => {
    try {
      setState(await run(() => post(`${t.base}/config-chat/reset`)));
      setReported(new Set());
    } catch {
      /* the toast already said what went wrong */
    }
  };

  const pending = (state?.changes ?? []).filter((c) => c.status === "pending");
  const decided = (state?.changes ?? []).filter((c) => c.status !== "pending").slice(0, 6);
  const examples = admin ? [...HOW_TO_EXAMPLES.slice(0, 3), ...CHANGE_EXAMPLES] : HOW_TO_EXAMPLES;

  // A link in an answer opens that page; on a phone the drawer covers it, so it steps aside.
  const onClick = (e) => {
    if (e.target.closest?.("a") && window.matchMedia("(max-width: 960px)").matches) setOpen(false);
  };

  return html`<aside class="assistant-drawer" role="dialog" aria-label=${tr("Assistant")} onClick=${onClick}>
    <div class="assistant-head">
      <div>
        <h2>${tr("Assistant")}</h2>
        <p class="muted small">
          ${admin
            ? tr("Ask how anything in the dashboard works, or say what to change about your AI agent. Nothing changes until you press Apply.")
            : tr("Ask how anything in the dashboard works. The answers come from the user guide.")}
        </p>
      </div>
      <div class="row-actions">
        ${state?.messages.length > 0 && html`<button class="btn small ghost" disabled=${busy} onClick=${newChat}>${tr("New chat")}</button>`}
        <button class="icon-btn" aria-label=${tr("Close")} onClick=${() => setOpen(false)}>×</button>
      </div>
    </div>
    <div class="assistant-body">
      ${!state && html`<${Loading} />`}
      ${state &&
      html`<div class="chat-log">
          ${!state.messages.length &&
          html`<div class="chat-examples">
            <p class="muted small">${tr("For example:")}</p>
            ${examples.map((e) => html`<button type="button" class="chip" dir="auto" onClick=${() => setDraft(tr(e))}>${tr(e)}</button>`)}
          </div>`}
          ${state.messages.map(
            (m, i) =>
              html`<div class="msg ${m.role === "user" ? "out" : "in"}" key=${i}>
                <div class="bubble" dir="auto">${m.role === "assistant" ? guideText(m.text) : m.text}</div>
                ${m.role === "assistant" &&
                html`<button type="button" class="link small msg-report" disabled=${busy || reported.has(i)} onClick=${() => report(i)}>
                  ${reported.has(i) ? tr("Reported") : tr("Report this answer")}
                </button>`}
              </div>`,
          )}
          ${busy && html`<div class="msg in"><div class="bubble typing"><span></span><span></span><span></span></div></div>`}
          <div ref=${bottom}></div>
        </div>
        ${pending.length > 0 &&
        html`<div class="assistant-changes">
          <div class="section-head tight">
            <h3>${tr("Waiting for you")}</h3>
            ${pending.length > 1 && html`<button class="btn small" disabled=${busy} onClick=${() => applyAll(pending)}>${tr("Apply all")}</button>`}
          </div>
          ${pending.map((c) => html`<${ChangeCard} key=${c.id} change=${c} busy=${busy} onApply=${() => decide(c, "apply")} onDismiss=${() => decide(c, "dismiss")} />`)}
        </div>`}
        ${decided.length > 0 &&
        html`<div class="assistant-changes">
          <div class="section-head tight"><h3>${tr("Recently decided")}</h3></div>
          ${decided.map((c) => html`<${ChangeCard} key=${c.id} change=${c} busy=${busy} />`)}
        </div>`}`}
    </div>
    <form class="composer" onSubmit=${send}>
      <textarea
        ref=${input}
        rows="2"
        dir="auto"
        placeholder=${admin ? tr("Ask a question, or say what to change") : tr("Ask how to do something")}
        value=${draft}
        onInput=${(e) => setDraft(e.target.value)}
        onKeyDown=${(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send(e);
          }
        }}
      ></textarea>
      <button class="btn primary" disabled=${busy || !draft.trim() || !state}>${busy ? tr("Thinking…") : tr("Send")}</button>
    </form>
  </aside>`;
}
